'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff, Loader2, CheckCircle, ExternalLink, Copy, Check } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


interface RegisterForm {
  nombre: string;
  email: string;
  password: string;
  notify: boolean;
}

interface LoginForm {
  email: string;
  password: string;
  remember: boolean;
}

const DEMO_CREDENTIALS = [
  { role: 'Seguidora', email: 'camila.rojas@gmail.com', password: 'chisme2026!' },
  { role: 'Anfitriona', email: 'anfitriona@chismeytejidos.cl', password: 'tejidos2026!' },
];

function CredentialBox({ onFill }: { onFill: (email: string, password: string) => void }) {
  const [copied, setCopied] = useState<string | null>(null);

  const handleCopy = (text: string, key: string) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(null), 1500);
  };

  return (
    <div className="mt-4 bg-muted/60 border border-border rounded-xl p-4">
      <p className="text-xs font-semibold text-muted-foreground mb-3 uppercase tracking-wider">
        Cuentas de demostración
      </p>
      <div className="flex flex-col gap-2">
        {DEMO_CREDENTIALS.map((cred) => (
          <div key={`cred-${cred.role}`} className="flex items-center justify-between gap-2 bg-card border border-border rounded-lg px-3 py-2">
            <div className="flex-1 min-w-0">
              <span className="text-xs font-bold text-primary">{cred.role}</span>
              <p className="text-xs text-muted-foreground truncate">{cred.email}</p>
            </div>
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => handleCopy(cred.email, `${cred.role}-email`)}
                className="p-1.5 rounded text-muted-foreground hover:text-primary transition-colors"
                aria-label="Copiar email"
              >
                {copied === `${cred.role}-email` ? <Check size={13} className="text-green-600" /> : <Copy size={13} />}
              </button>
              <button
                onClick={() => onFill(cred.email, cred.password)}
                className="text-xs bg-primary text-white px-2.5 py-1 rounded-lg font-semibold hover:bg-primary/90 transition-colors"
              >
                Usar
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AuthForm() {
  const [tab, setTab] = useState<'login' | 'register'>('login');
  const [showPass, setShowPass] = useState(false);
  const [loginDone, setLoginDone] = useState(false);
  const [registerDone, setRegisterDone] = useState(false);
  const [loginError, setLoginError] = useState('');

  const loginForm = useForm<LoginForm>({ defaultValues: { remember: false } });
  const registerForm = useForm<RegisterForm>({ defaultValues: { notify: true } });

  const fillCredentials = (email: string, password: string) => {
    loginForm.setValue('email', email);
    loginForm.setValue('password', password);
    setLoginError('');
  };

  const onLogin = async (data: LoginForm) => {
    // BACKEND: POST /api/auth/login — validate credentials and return session token
    await new Promise((r) => setTimeout(r, 1000));
    const valid = DEMO_CREDENTIALS.some(
      (c) => c.email === data.email && c.password === data.password
    );
    if (!valid) {
      setLoginError('Credenciales inválidas — usa las cuentas de demo abajo para ingresar');
      return;
    }
    setLoginDone(true);
  };

  const onRegister = async (data: RegisterForm) => {
    // BACKEND: POST /api/auth/register — create user account and send confirmation email
    await new Promise((r) => setTimeout(r, 1200));
    setRegisterDone(true);
  };

  return (
    <div className="bg-card border border-border rounded-3xl p-8 shadow-sm">
      {/* Tabs */}
      <div className="flex gap-1 bg-muted rounded-xl p-1 mb-6">
        {(['login', 'register'] as const).map((t) => (
          <button
            key={`tab-${t}`}
            onClick={() => setTab(t)}
            className={`flex-1 py-2.5 rounded-lg text-sm font-semibold transition-all ${
              tab === t
                ? 'bg-card text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {t === 'login' ? '🔑 Iniciar sesión' : '🧶 Registrarse'}
          </button>
        ))}
      </div>

      {/* Login Form */}
      {tab === 'login' && (
        <>
          {loginDone ? (
            <div className="text-center py-6">
              <div className="text-5xl mb-3">🎉</div>
              <h3 className="text-lg font-bold text-foreground mb-1">¡Bienvenida de vuelta!</h3>
              <p className="text-sm text-muted-foreground">Ya estás dentro. ¡A chismear! 🧶</p>
            </div>
          ) : (
            <form onSubmit={loginForm.handleSubmit(onLogin)} className="flex flex-col gap-4">
              <div>
                <label className="block text-sm font-semibold text-foreground mb-1.5">Email</label>
                <input
                  {...loginForm.register('email', {
                    required: 'El email es obligatorio',
                    pattern: { value: /\S+@\S+\.\S+/, message: 'Email inválido' },
                  })}
                  type="email"
                  placeholder="tucorreo@gmail.com"
                  className={`w-full bg-input border rounded-xl px-4 py-3 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 transition ${
                    loginForm.formState.errors.email ? 'border-red-400' : 'border-border'
                  }`}
                />
                {loginForm.formState.errors.email && (
                  <p className="text-red-500 text-xs mt-1">{loginForm.formState.errors.email.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-foreground mb-1.5">Contraseña</label>
                <div className="relative">
                  <input
                    {...loginForm.register('password', { required: 'La contraseña es obligatoria' })}
                    type={showPass ? 'text' : 'password'}
                    placeholder="••••••••"
                    className={`w-full bg-input border rounded-xl px-4 py-3 pr-11 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 transition ${
                      loginForm.formState.errors.password ? 'border-red-400' : 'border-border'
                    }`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPass(!showPass)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    aria-label={showPass ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                  >
                    {showPass ? <EyeOff size={17} /> : <Eye size={17} />}
                  </button>
                </div>
                {loginForm.formState.errors.password && (
                  <p className="text-red-500 text-xs mt-1">{loginForm.formState.errors.password.message}</p>
                )}
              </div>

              <label className="flex items-center gap-2 cursor-pointer">
                <input {...loginForm.register('remember')} type="checkbox" className="w-4 h-4 rounded accent-primary" />
                <span className="text-sm text-muted-foreground">Recordarme</span>
              </label>

              {loginError && (
                <div className="bg-red-50 border border-red-200 text-red-700 text-xs rounded-xl px-4 py-3">
                  {loginError}
                </div>
              )}

              <button
                type="submit"
                disabled={loginForm.formState.isSubmitting}
                className="btn-primary flex items-center justify-center gap-2 py-3.5 rounded-xl font-bold text-sm disabled:opacity-60"
              >
                {loginForm.formState.isSubmitting ? (
                  <><Loader2 size={16} className="animate-spin" /> Entrando...</>
                ) : (
                  '🔑 Iniciar sesión'
                )}
              </button>

              <CredentialBox onFill={fillCredentials} />
            </form>
          )}
        </>
      )}

      {/* Register Form */}
      {tab === 'register' && (
        <>
          {registerDone ? (
            <div className="text-center py-6">
              <div className="text-5xl mb-3">🎊</div>
              <h3 className="text-lg font-bold text-foreground mb-1">¡Ya eres parte de la comunidad!</h3>
              <p className="text-sm text-muted-foreground mb-3">Revisa tu email para confirmar tu cuenta.</p>
              <div className="flex items-center justify-center gap-2 text-green-600 text-sm font-semibold">
                <CheckCircle size={16} />
                Te avisaremos cuando haya un live 🧶
              </div>
            </div>
          ) : (
            <form onSubmit={registerForm.handleSubmit(onRegister)} className="flex flex-col gap-4">
              <div>
                <label className="block text-sm font-semibold text-foreground mb-1.5">Tu nombre</label>
                <input
                  {...registerForm.register('nombre', { required: 'El nombre es obligatorio' })}
                  type="text"
                  placeholder="¿Cómo te llamamos?"
                  className={`w-full bg-input border rounded-xl px-4 py-3 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 transition ${
                    registerForm.formState.errors.nombre ? 'border-red-400' : 'border-border'
                  }`}
                />
                {registerForm.formState.errors.nombre && (
                  <p className="text-red-500 text-xs mt-1">{registerForm.formState.errors.nombre.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-foreground mb-1.5">Email</label>
                <input
                  {...registerForm.register('email', {
                    required: 'El email es obligatorio',
                    pattern: { value: /\S+@\S+\.\S+/, message: 'Email inválido' },
                  })}
                  type="email"
                  placeholder="tucorreo@gmail.com"
                  className={`w-full bg-input border rounded-xl px-4 py-3 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 transition ${
                    registerForm.formState.errors.email ? 'border-red-400' : 'border-border'
                  }`}
                />
                {registerForm.formState.errors.email && (
                  <p className="text-red-500 text-xs mt-1">{registerForm.formState.errors.email.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-foreground mb-1.5">Contraseña</label>
                <div className="relative">
                  <input
                    {...registerForm.register('password', {
                      required: 'La contraseña es obligatoria',
                      minLength: { value: 8, message: 'Mínimo 8 caracteres' },
                    })}
                    type={showPass ? 'text' : 'password'}
                    placeholder="Mínimo 8 caracteres"
                    className={`w-full bg-input border rounded-xl px-4 py-3 pr-11 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 transition ${
                      registerForm.formState.errors.password ? 'border-red-400' : 'border-border'
                    }`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPass(!showPass)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    aria-label={showPass ? 'Ocultar' : 'Mostrar'}
                  >
                    {showPass ? <EyeOff size={17} /> : <Eye size={17} />}
                  </button>
                </div>
                {registerForm.formState.errors.password && (
                  <p className="text-red-500 text-xs mt-1">{registerForm.formState.errors.password.message}</p>
                )}
              </div>

              <label className="flex items-start gap-3 cursor-pointer">
                <input {...registerForm.register('notify')} type="checkbox" className="mt-0.5 w-4 h-4 rounded accent-primary" />
                <span className="text-sm text-muted-foreground">
                  <span className="text-foreground font-medium">Avísame cuando haya un live</span> — te enviamos un email cuando la anfi esté transmitiendo
                </span>
              </label>

              <button
                type="submit"
                disabled={registerForm.formState.isSubmitting}
                className="btn-primary flex items-center justify-center gap-2 py-3.5 rounded-xl font-bold text-sm disabled:opacity-60"
              >
                {registerForm.formState.isSubmitting ? (
                  <><Loader2 size={16} className="animate-spin" /> Creando cuenta...</>
                ) : (
                  '🧶 Crear mi cuenta gratis'
                )}
              </button>

              {/* Social follow */}
              <div className="pt-2 border-t border-border">
                <p className="text-xs text-center text-muted-foreground mb-3">O síguenos en redes</p>
                <div className="flex gap-2 justify-center">
                  {[
                    { label: 'Instagram', icon: ExternalLink },
                    { label: 'YouTube', icon: ExternalLink },
                    { label: 'TikTok', icon: ExternalLink },
                  ].map((s) => {
                    const Icon = s.icon;
                    return (
                      <button
                        key={`social-reg-${s.label}`}
                        type="button"
                        className="flex items-center gap-1.5 bg-muted border border-border px-3 py-2 rounded-xl text-xs font-medium text-muted-foreground hover:text-primary hover:border-primary/30 transition-all"
                      >
                        <Icon size={14} />
                        {s.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </form>
          )}
        </>
      )}
    </div>
  );
}